import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard, Search, Filter, RefreshCw, ChevronDown, Eye, 
  AlertTriangle, Check, X, Settings, Link as LinkIcon, Webhook,
  Plus, Trash2, Save, Edit2, DollarSign, Users, Clock, 
  ExternalLink, Copy, CheckCircle, XCircle
} from 'lucide-react';
import { adminAPI } from '../../api';
import toast from 'react-hot-toast';

// ============ Payments List Tab ============
const PaymentsList = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', type: '' });

  useEffect(() => {
    fetchPayments();
  }, [filters]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPayments(filters);
      setPayments(response.data?.payments || []);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      captured: 'bg-green-100 text-green-700',
      verified: 'bg-blue-100 text-blue-700',
      created: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      unmapped: 'bg-orange-100 text-orange-700',
      activated: 'bg-purple-100 text-purple-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="captured">Captured</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="unmapped">Unmapped</option>
        </select>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Types</option>
          <option value="topup">Credit Top-up</option>
          <option value="subscription_upgrade">Subscription</option>
          <option value="subscription_charge">Renewal</option>
        </select>
        <button
          onClick={fetchPayments}
          className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Payments Table/Cards */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No payments found
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Payment ID</th>
                    <th className="text-left p-4 font-medium text-slate-600">Partner</th>
                    <th className="text-left p-4 font-medium text-slate-600">Type</th>
                    <th className="text-right p-4 font-medium text-slate-600">Amount</th>
                    <th className="text-right p-4 font-medium text-slate-600">Credits</th>
                    <th className="text-center p-4 font-medium text-slate-600">Status</th>
                    <th className="text-left p-4 font-medium text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payments.map((payment) => (
                    <tr key={payment.payment_id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {payment.payment_id?.substring(0, 20)}...
                        </code>
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-slate-800">{payment.partner_name || 'Unknown'}</div>
                        <div className="text-xs text-slate-500">{payment.partner_id?.substring(0, 8)}...</div>
                      </td>
                      <td className="p-4">
                        <span className="capitalize">{payment.payment_type?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="p-4 text-right font-medium">₹{payment.amount_inr?.toLocaleString()}</td>
                      <td className="p-4 text-right font-medium text-green-600">+{payment.credits?.toLocaleString()}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(payment.status)}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 text-xs">
                        {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-slate-100">
              {payments.map((payment) => (
                <div key={payment.payment_id} className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 text-sm truncate">{payment.partner_name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500 capitalize">{payment.payment_type?.replace(/_/g, ' ')}</p>
                    </div>
                    <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(payment.status)}`}>
                      {payment.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-xs text-slate-500">Amount</p>
                      <p className="font-medium">₹{payment.amount_inr?.toLocaleString()}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2">
                      <p className="text-xs text-emerald-600">Credits</p>
                      <p className="font-medium text-emerald-700">+{payment.credits?.toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '-'}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============ Unmapped Payments Tab ============
const UnmappedPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(null);
  const [mappingData, setMappingData] = useState({
    partner_id: '',
    credits_to_add: 0,
    plan_id: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUnmapped();
  }, []);

  const fetchUnmapped = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUnmappedPayments();
      setPayments(response.data?.payments || []);
    } catch (error) {
      toast.error('Failed to load unmapped payments');
    } finally {
      setLoading(false);
    }
  };

  const handleMap = async () => {
    if (!mappingData.partner_id) {
      toast.error('Please enter partner ID');
      return;
    }

    try {
      setSubmitting(true);
      await adminAPI.mapPayment({
        payment_id: mapping.payment_id,
        ...mappingData
      });
      toast.success('Payment mapped successfully!');
      setMapping(null);
      setMappingData({ partner_id: '', credits_to_add: 0, plan_id: '', notes: '' });
      fetchUnmapped();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to map payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-slate-600 text-sm">
          {payments.length} payment(s) need manual mapping
        </p>
        <button
          onClick={fetchUnmapped}
          className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : payments.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="text-green-700 font-medium">All payments are mapped!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {payments.map((payment) => (
            <div key={payment.payment_id} className="bg-white rounded-xl border border-orange-200 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className="font-medium text-slate-800">Unmapped Payment</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-slate-500">Payment ID:</span> <code className="bg-slate-100 px-1 rounded">{payment.payment_id}</code></p>
                    <p><span className="text-slate-500">Amount:</span> ₹{payment.amount_inr?.toLocaleString()}</p>
                    <p><span className="text-slate-500">Type:</span> {payment.payment_type}</p>
                    <p><span className="text-slate-500">Date:</span> {payment.created_at ? new Date(payment.created_at).toLocaleString() : '-'}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMapping(payment);
                    setMappingData({
                      partner_id: '',
                      credits_to_add: payment.credits || payment.amount_inr || 0,
                      plan_id: payment.plan_id || '',
                      notes: ''
                    });
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Map to Partner
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mapping Modal */}
      <AnimatePresence>
        {mapping && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setMapping(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Map Payment to Partner</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Partner ID *</label>
                  <input
                    type="text"
                    value={mappingData.partner_id}
                    onChange={(e) => setMappingData({ ...mappingData, partner_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter partner ID or email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Credits to Add</label>
                  <input
                    type="number"
                    value={mappingData.credits_to_add}
                    onChange={(e) => setMappingData({ ...mappingData, credits_to_add: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Plan ID (optional)</label>
                  <select
                    value={mappingData.plan_id}
                    onChange={(e) => setMappingData({ ...mappingData, plan_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No plan change</option>
                    <option value="growth">Growth</option>
                    <option value="professional">Professional</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    value={mappingData.notes}
                    onChange={(e) => setMappingData({ ...mappingData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Add any notes..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setMapping(null)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMap}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Mapping...' : 'Confirm & Credit'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ Credit Packs Config Tab ============
const CreditPacksConfig = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [minRechargeAmount, setMinRechargeAmount] = useState(500);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchPacks();
    fetchPaymentSettings();
  }, []);

  const fetchPacks = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCreditPacksConfig();
      setPacks(response.data?.packs || []);
    } catch (error) {
      toast.error('Failed to load credit packs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await adminAPI.getPaymentSettings();
      setMinRechargeAmount(response.data?.min_recharge_amount || 500);
    } catch (error) {
      console.error('Failed to load payment settings');
    }
  };

  const handleSaveMinRecharge = async () => {
    try {
      setSavingSettings(true);
      await adminAPI.updatePaymentSettings({ min_recharge_amount: minRechargeAmount });
      toast.success(`Minimum recharge updated to ₹${minRechargeAmount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminAPI.updateCreditPacksConfig({ packs });
      toast.success('Credit packs updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addPack = () => {
    const newPack = {
      pack_id: `pack_${Date.now()}`,
      name: 'New Pack',
      amount_inr: 1000,
      credits: 1000,
      bonus_credits: 0,
      is_active: true,
      popular: false,
      sort_order: packs.length + 1
    };
    setPacks([...packs, newPack]);
    setEditingPack(newPack.pack_id);
  };

  const updatePack = (packId, field, value) => {
    setPacks(packs.map(p => 
      p.pack_id === packId ? { ...p, [field]: value } : p
    ));
  };

  const deletePack = (packId) => {
    setPacks(packs.filter(p => p.pack_id !== packId));
  };

  return (
    <div className="space-y-6">
      {/* Payment Settings Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Payment Settings
        </h3>
        <div className="flex items-center gap-4">
          <label className="text-sm text-slate-600">Minimum Recharge Amount (₹):</label>
          <input
            type="number"
            value={minRechargeAmount}
            onChange={(e) => setMinRechargeAmount(Math.max(100, parseInt(e.target.value) || 100))}
            min="100"
            step="100"
            className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSaveMinRecharge}
            disabled={savingSettings}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {savingSettings ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Partners must recharge at least this amount. Credit pricing is 1:1 (₹1 = 1 credit).</p>
      </div>

      {/* Credit Packs Section */}
      <div className="flex items-center justify-between">
        <p className="text-slate-600 text-sm">Configure credit packs available for purchase (1:1 pricing)</p>
        <div className="flex gap-2">
          <button
            onClick={addPack}
            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Pack
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {packs.map((pack, index) => (
            <div key={pack.pack_id} className="bg-white rounded-xl border border-slate-200 p-4">
              {/* Desktop Grid Layout */}
              <div className="hidden sm:grid grid-cols-2 md:grid-cols-6 gap-4 items-center">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Pack Name</label>
                  <input
                    type="text"
                    value={pack.name}
                    onChange={(e) => updatePack(pack.pack_id, 'name', e.target.value)}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Price (₹)</label>
                  <input
                    type="number"
                    value={pack.amount_inr}
                    onChange={(e) => updatePack(pack.pack_id, 'amount_inr', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Credits</label>
                  <input
                    type="number"
                    value={pack.credits}
                    onChange={(e) => updatePack(pack.pack_id, 'credits', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Bonus</label>
                  <input
                    type="number"
                    value={pack.bonus_credits}
                    onChange={(e) => updatePack(pack.pack_id, 'bonus_credits', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={pack.is_active}
                      onChange={(e) => updatePack(pack.pack_id, 'is_active', e.target.checked)}
                      className="rounded"
                    />
                    Active
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={pack.popular}
                      onChange={(e) => updatePack(pack.pack_id, 'popular', e.target.checked)}
                      className="rounded"
                    />
                    Popular
                  </label>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => deletePack(pack.pack_id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Mobile Stacked Layout */}
              <div className="sm:hidden space-y-3">
                <div className="flex items-center justify-between">
                  <input
                    type="text"
                    value={pack.name}
                    onChange={(e) => updatePack(pack.pack_id, 'name', e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-slate-200 rounded text-sm font-medium focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Pack Name"
                  />
                  <button
                    onClick={() => deletePack(pack.pack_id)}
                    className="ml-2 p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Price (₹)</label>
                    <input
                      type="number"
                      value={pack.amount_inr}
                      onChange={(e) => updatePack(pack.pack_id, 'amount_inr', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Credits</label>
                    <input
                      type="number"
                      value={pack.credits}
                      onChange={(e) => updatePack(pack.pack_id, 'credits', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Bonus</label>
                    <input
                      type="number"
                      value={pack.bonus_credits}
                      onChange={(e) => updatePack(pack.pack_id, 'bonus_credits', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={pack.is_active}
                      onChange={(e) => updatePack(pack.pack_id, 'is_active', e.target.checked)}
                      className="rounded"
                    />
                    Active
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={pack.popular}
                      onChange={(e) => updatePack(pack.pack_id, 'popular', e.target.checked)}
                      className="rounded"
                    />
                    Popular
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Subscription Links Config Tab ============
const SubscriptionLinksConfig = () => {
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSubscriptionLinksConfig();
      setLinks(response.data?.links || {});
    } catch (error) {
      toast.error('Failed to load subscription links');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminAPI.updateSubscriptionLinksConfig({ links });
      toast.success('Subscription links updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const plans = [
    { id: 'growth', name: 'Growth Plan', description: '₹2,999/month - ₹1,500 usage balance' },
    { id: 'professional', name: 'Professional Plan', description: '₹7,999/month - ₹3,000 usage balance' }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-slate-600 text-sm">Configure Razorpay payment links for subscription upgrades</p>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-slate-800">{plan.name}</h4>
                  <p className="text-sm text-slate-500">{plan.description}</p>
                </div>
                {links[plan.id] && (
                  <a
                    href={links[plan.id]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Test Link
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-slate-400" />
                <input
                  type="url"
                  value={links[plan.id] || ''}
                  onChange={(e) => setLinks({ ...links, [plan.id]: e.target.value })}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://rzp.io/rzp/..."
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Webhook Logs Tab ============
const WebhookLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getWebhookLogs(100);
      setLogs(response.data?.logs || []);
    } catch (error) {
      toast.error('Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-slate-600 text-sm">Recent Razorpay webhook events</p>
        <button
          onClick={fetchLogs}
          className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No webhook logs found
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto max-h-[500px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600">Event ID</th>
                    <th className="text-left p-3 font-medium text-slate-600">Event Type</th>
                    <th className="text-center p-3 font-medium text-slate-600">Processed</th>
                    <th className="text-left p-3 font-medium text-slate-600">Result</th>
                    <th className="text-left p-3 font-medium text-slate-600">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => (
                    <tr key={log.event_id} className="hover:bg-slate-50">
                      <td className="p-3">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {log.event_id?.substring(0, 16)}...
                        </code>
                      </td>
                      <td className="p-3 font-medium text-slate-700">{log.event_type}</td>
                      <td className="p-3 text-center">
                        {log.processed ? (
                          <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 mx-auto" />
                        )}
                      </td>
                      <td className="p-3 text-slate-500 text-xs max-w-[200px] truncate">
                        {log.result_message || '-'}
                      </td>
                      <td className="p-3 text-slate-500 text-xs">
                        {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
              {logs.map((log) => (
                <div key={log.event_id} className="p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="font-medium text-slate-700 text-sm">{log.event_type}</span>
                    {log.processed ? (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle className="w-3 h-3" /> Processed
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-red-600">
                        <XCircle className="w-3 h-3" /> Failed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate mb-1">{log.result_message || '-'}</p>
                  <p className="text-xs text-slate-400">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============ Main Payments Page ============
const Payments = () => {
  const [activeTab, setActiveTab] = useState('payments');

  const tabs = [
    { id: 'payments', label: 'All Payments', shortLabel: 'Payments', icon: CreditCard },
    { id: 'unmapped', label: 'Unmapped', shortLabel: 'Unmapped', icon: AlertTriangle },
    { id: 'credit-packs', label: 'Credit Packs', shortLabel: 'Packs', icon: DollarSign },
    { id: 'subscription-links', label: 'Sub Links', shortLabel: 'Links', icon: LinkIcon },
    { id: 'webhooks', label: 'Webhooks', shortLabel: 'Hooks', icon: Webhook }
  ];

  return (
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden" data-testid="admin-payments-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Payments Management</h1>
        <p className="text-sm text-slate-600">Manage payments, credit packs, and subscriptions</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 rounded-lg font-medium text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.shortLabel || tab.label.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'payments' && <PaymentsList />}
          {activeTab === 'unmapped' && <UnmappedPayments />}
          {activeTab === 'credit-packs' && <CreditPacksConfig />}
          {activeTab === 'subscription-links' && <SubscriptionLinksConfig />}
          {activeTab === 'webhooks' && <WebhookLogs />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Payments;
